import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { BASE_REGIONS, QUOTE_SERVICES, REGIONS } from "@/lib/services";

const EMPTY = { name: "", email: "", phone: "", region: "", make: "", model: "", plate: "", description: "" };

function Field({ label, k, form, set, type = "text", required = true }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="field" type={type} required={required} value={form[k]} data-testid={`quote-${k}-input`}
        onChange={(e) => set({ ...form, [k]: e.target.value })} />
    </div>
  );
}

export default function QuoteForm({ service, setService }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const estimate = form.region && (BASE_REGIONS.includes(form.region)
    ? "Costo medio perizia circa €500"
    : "Costo medio perizia circa €500 + costi di spostamento");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { make, model, plate, ...rest } = form;
      const { data } = await api.post("/quotes", { ...rest, service, vehicle: { make, model, plate } });
      setDone(data);
      setForm(EMPTY);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="surface p-8 text-center" data-testid="quote-success">
        <CheckCircle2 className="mx-auto text-green-400 mb-3" size={44} />
        <p className="text-lg text-white">Richiesta #{done.number} inviata.</p>
        <p className="text-slate-400 text-sm mt-2">Ti ricontatteremo con un preventivo. {done.estimate}.</p>
        <button onClick={() => setDone(null)} className="btn-ghost px-5 py-2.5 text-sm mt-6" data-testid="quote-new-button">Nuova richiesta</button>
      </div>
    );

  return (
    <form onSubmit={submit} className="surface p-5 sm:p-8 space-y-5" data-testid="quote-form">
      <div>
        <label className="label">Servizio richiesto</label>
        <div className="grid sm:grid-cols-2 gap-2">
          {QUOTE_SERVICES.map((s) => (
            <button type="button" key={s.key} onClick={() => setService(s.key)} data-testid={`quote-service-${s.key}`}
              className={`slot-btn !font-sans text-left px-3 py-2.5 text-sm flex items-center gap-2 ${service === s.key ? "active" : ""}`}>
              <s.icon size={16} className="shrink-0" /> {s.title}
            </button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome e cognome / Studio" k="name" form={form} set={setForm} />
        <div>
          <label className="label">Regione</label>
          <select className="field" required value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="quote-region-select">
            <option value="">Seleziona…</option>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Field label="Email" k="email" type="email" form={form} set={setForm} />
        <Field label="Telefono / WhatsApp" k="phone" type="tel" form={form} set={setForm} />
        <Field label="Marca veicolo (opzionale)" k="make" form={form} set={setForm} required={false} />
        <Field label="Modello (opzionale)" k="model" form={form} set={setForm} required={false} />
        <Field label="Targa (opzionale)" k="plate" form={form} set={setForm} required={false} />
      </div>
      <div>
        <label className="label">Descrivi il caso</label>
        <textarea className="field min-h-[120px]" required minLength={10} value={form.description} data-testid="quote-description-input"
          placeholder="Tipo di incarico, tribunale o controparte se presenti, stato del veicolo, tempistiche…"
          onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>
      {estimate && <div className="surface-2 px-4 py-3 text-sm text-slate-200" data-testid="quote-estimate">{estimate}. Il preventivo definitivo ti verrà inviato dopo l'analisi del caso.</div>}
      {error && <div className="text-sm text-red-400" data-testid="quote-error">{error}</div>}
      <button className="btn-red w-full sm:w-auto px-8 py-3 flex items-center justify-center gap-2" disabled={busy || !service} data-testid="quote-submit-button">
        {busy && <Loader2 size={16} className="animate-spin" />} Richiedi preventivo gratuito
      </button>
    </form>
  );
}
